<script setup>
import IconAccountSettings from "~/svg/nav-account-settings.svg";
import IconPassword from "~/svg/nav-password.svg";
import IconBrowserSessions from "~/svg/nav-browser-sessions.svg";
import IconDeleteAccount from "~/svg/nav-delete-account.svg";

/**
 * Manage Account sidebar (desktop) / tab bar (mobile), per Figma 271-1000 /
 * 271-1090 (GH-922). Sections live on one page, so items switch client-side
 * through the `select` event rather than navigating.
 */
defineProps({
    active: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(["select"]);

// Each icon keeps its natural Figma proportions inside a fixed-width slot
// so the labels align; forcing one box distorts them.
const items = [
    {
        key: "settings",
        label: "Account Settings",
        icon: IconAccountSettings,
        iconClass: "h-[17px] w-5",
    },
    {
        key: "password",
        label: "Password",
        icon: IconPassword,
        iconClass: "h-3 w-[22px]",
    },
    {
        key: "sessions",
        label: "Browser Sessions",
        icon: IconBrowserSessions,
        iconClass: "h-[18px] w-[22px]",
    },
    {
        key: "delete",
        label: "Delete Account",
        icon: IconDeleteAccount,
        iconClass: "h-[18px] w-4",
    },
];
</script>

<template>
    <div>
        <!-- Desktop sidebar: cream panel overlapping the hero band -->
        <!-- max-md:hidden (not `hidden md:flex`): legacy .hidden is !important on pages that load app.scss -->
        <aside
            class="relative z-10 flex h-full flex-col rounded-brand bg-brand-cream p-6 max-md:hidden"
        >
            <h2 class="font-brand text-[22px] font-semibold text-brand-navy">
                Manage Account
            </h2>

            <nav class="mt-5">
                <ul class="flex flex-col gap-1">
                    <li v-for="item in items" :key="item.key">
                        <button
                            type="button"
                            class="flex w-full items-center gap-4 rounded-brand px-4 py-4 text-left font-brand text-[15px] font-bold text-brand-navy transition"
                            :class="
                                active === item.key
                                    ? 'bg-white shadow-sm'
                                    : 'hover:bg-white/60'
                            "
                            @click="emit('select', item.key)"
                        >
                            <!-- Red accents (currentColor in the SVGs) show
                                 only on the active item -->
                            <span
                                class="grid w-6 shrink-0 place-items-center"
                                :class="
                                    active === item.key
                                        ? 'text-brand-red'
                                        : 'text-brand-navy'
                                "
                                aria-hidden="true"
                            >
                                <component
                                    :is="item.icon"
                                    :class="item.iconClass"
                                />
                            </span>
                            {{ item.label }}
                        </button>
                    </li>
                </ul>
            </nav>
        </aside>

        <!-- Mobile: horizontal tabs with active underline. The row scrolls
             sideways; the right-edge fade signals the off-screen tabs
             (Delete Account sits past the fold on phones, GH-1057). -->
        <div class="relative mt-4 max-md:block md:hidden">
            <span
                aria-hidden="true"
                class="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-white to-transparent"
            ></span>
            <nav
                class="-mx-4 overflow-x-auto border-b border-brand-tan/60 px-4"
                aria-label="Manage Account"
            >
                <ul class="flex items-stretch gap-6">
                    <li v-for="item in items" :key="item.key">
                        <button
                            type="button"
                            class="flex items-center gap-2 whitespace-nowrap border-b-4 pb-3 font-brand text-[15px] font-bold text-brand-navy"
                            :class="
                                active === item.key
                                    ? 'border-[#ffaa4a]'
                                    : 'border-transparent'
                            "
                            @click="emit('select', item.key)"
                        >
                            <component
                                :is="item.icon"
                                v-if="active === item.key"
                                class="shrink-0 scale-[0.85] text-brand-red"
                                :class="item.iconClass"
                            />
                            {{ item.label }}
                        </button>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>
